import { ChangeDetectionStrategy, Component, inject, OnInit, output, signal } from '@angular/core';
import { defaultDateRange, periodSelectData } from "../../configs/daily-feedback-reports-filters-config";
import { FormControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { DailyFeedbackReportsFiltersService } from "../../services/daily-feedback-reports-filters.service";
import { DateRange } from "../../types/daily-feedback-reports-page.types";
import { DEFAULT_ZOOM, ZOOM_OPTIONS } from "../../../../../core/constants/constants";
import { customDateProvider } from "../../../../../core/locale/providers/custom-date.provider";
import { MatFormField, MatInput, MatLabel, MatSuffix } from "@angular/material/input";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { DateInputDirective } from "../../../../../shared/directives/date-input.directive";
import { MatOption, MatSelect } from "@angular/material/select";
import { MatIcon } from "@angular/material/icon";

@Component({
  selector: 'app-daily-feedback-reports-filters',
  standalone: true,
  templateUrl: './daily-feedback-reports-filters.component.html',
  styleUrl: './daily-feedback-reports-filters.component.scss',
  providers: [ ...customDateProvider ],
  imports: [
    MatFormField,
    MatLabel,
    DateInputDirective,
    MatDatepickerModule,
    ReactiveFormsModule,
    MatInput,
    DateInputDirective,
    MatSuffix,
    MatSelect,
    MatOption,
    MatIcon
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DailyFeedbackReportsFiltersComponent implements OnInit {
  handleGetData = output<DateRange>();
  updateGroupStyles = output<number>();

  private dailyFeedbackReportsFiltersService: DailyFeedbackReportsFiltersService = inject(DailyFeedbackReportsFiltersService);

  public readonly periodSelectData = periodSelectData;
  public readonly ZOOM_OPTIONS = ZOOM_OPTIONS;
  public currentZoomOption = signal<number>(DEFAULT_ZOOM);
  public fastPeriodControl = new FormControl('');
  public rangeForm = new FormGroup({
    dateFrom: new FormControl(''),
    dateTo: new FormControl('')
  });

  public ngOnInit(): void {
    this.setDefaultDates();
  }

  public handleDateRangeChange(): void {
    this.fastPeriodControl.reset();
    this.getData();
  }

  public clearDateRange(): void {
    this.rangeForm.reset();
    this.fastPeriodControl.reset();
    this.handleDateRangeChange();
  }

  public handleSelectChange(value: string): void {
    if (!value) {
      this.clearDateRange();
      return;
    }

    const today = this.dailyFeedbackReportsFiltersService.getCurrentDate();
    const strategy = this.dailyFeedbackReportsFiltersService.dateStrategies[value as keyof typeof this.dailyFeedbackReportsFiltersService.dateStrategies];
    if (!strategy) { return; }

    const dates = strategy(today);
    if (!dates) { return; }

    this.updateRangeForm(dates.start, dates.end);
    this.getData();
  }

  public handleZoomSelectValueChange(): void {
    this.updateGroupStyles.emit(this.currentZoomOption());
  }

  private setDefaultDates(): void {
    const today = this.dailyFeedbackReportsFiltersService.getCurrentDate();
    this.updateRangeForm(today, today);
    this.handleDateRangeChange();
  }

  private updateRangeForm(dateFrom: Date, dateTo: Date): void {
    this.rangeForm.patchValue({
      dateFrom: this.dailyFeedbackReportsFiltersService.formatDate(dateFrom),
      dateTo: this.dailyFeedbackReportsFiltersService.formatDate(dateTo)
    });
  }

  private getData(): void {
    if (!this.rangeForm.value.dateFrom || !this.rangeForm.value.dateTo) {
      this.handleGetData.emit(defaultDateRange);
      return;
    }

    const dateRange: DateRange = {
      dateFrom: this.dailyFeedbackReportsFiltersService.formatDate(new Date(this.rangeForm.value.dateFrom)),
      dateTo: this.dailyFeedbackReportsFiltersService.formatDate(new Date(this.rangeForm.value.dateTo))
    };

    this.handleGetData.emit(dateRange);
  }
}
